import React from 'react';
import GuideHero from 'components/guide-hero';
import {
  CitationBlock,
  CitationBlockMini,
  CitationExampleBox,
  GuideHeader,
  GuideSection,
  GuideTitle,
  GuideWrapper,
  IntextCitation,
  LargeText,
  TextWithColor,
} from 'components/guide';
import GuideLinkButton, {
  GuideLinkButtonSection,
} from 'components/guide-link-button';
import Footer from 'components/footer';

const OscolaGuide: React.FC = () => (
  <>
    <GuideHero
      header="Our Complete Guide to Citing in OSCOLA Format"
      imgSrc={require('images/oscola-modal.png')}
      imgAlt="Laptop with screenshot on screen of manual for OSCOLA format"
    >
      <p>
        Welcome aboard our easy-to-understand guide, designed to walk you
        through the winding roads of the OSCOLA citation style. We&apos;ll be
        your co-pilots, helping you chart the terrain of footnotes and develop a
        comprehensive bibliography with simplicity.
      </p>
      <p>
        Let&apos;s dive into how an OSCOLA citation generator can be your secret
        weapon in streamlining your referencing process and unravel the
        mysteries of perfect footnote and reference formatting.{' '}
      </p>
      <p>We promise that creating an OSCOLA bibliography will be a breeze!</p>
    </GuideHero>
    <GuideWrapper>
      <GuideTitle>Discover More Bibcitation Guides</GuideTitle>
      <GuideLinkButtonSection>
        <GuideLinkButton text="MLA Guide" href="/mla" variant="purple" />
        <GuideLinkButton text="APA Guide" href="/apa" variant="blue" />
        <GuideLinkButton
          text="Chicago Guide"
          href="/chicago"
          variant="orange"
        />
      </GuideLinkButtonSection>
      <FormatOverview />
      <FormatFootnotes />
      <Bibliography />
      <BookCitations />
      <JournalCitations />
      <WebsiteCitations />
      <BookChapterCitations />
      <NewspaperMagazineCitations />
      <ConferencePaperCitations />
      <ThesisDissertationCitations />
      <SocialMediaCitations />
      <LegalCaseCitations />
      <PrimaryLegislationCitations />
      <EULegalSourceCitations />
      <OtherJurisdictionCitations />
      <HansardCitations />
      <EncyclopediaCitations />
      <InterviewCitations />
      <CaseNoteCitations />
      <PersonalCommunicationCitations />
      <FinalNotes />
    </GuideWrapper>
    <Footer />
  </>
);

const FormatOverview: React.FC = () => (
  <GuideSection id="Overview">
    <GuideHeader>OSCOLA Referencing Style Overview</GuideHeader>
    <p>
      The OSCOLA referencing format is widely accepted within legal academia and
      legal publications. It provides a standardized approach to referencing
      legal documents and cases, ensuring that the citations are clear,
      consistent, and easily traceable. By following OSCOLA guidelines, your
      legal arguments will be underpinned by properly attributed sources,
      thereby adding credibility and professionalism to your work.
    </p>
    <p>
      In the OSCOLA system, there are two primary aspects of citation: footnotes
      and the bibliography.
    </p>
    <p>
      <strong>Footnotes:</strong> Footnotes in OSCOLA are used for citing the
      sources of information or the legal authorities being referred to within
      the main text. These are detailed references that include the specific
      parts of the source being cited, such as page numbers or paragraph numbers
      in a judgment.
    </p>
    <p>
      <strong>Bibliography:</strong> The bibliography in OSCOLA gathers all the
      sources cited in footnotes and lists them at the end of the document. The
      bibliography is separated into tables, cases, legislation, and secondary
      sources.
    </p>
    <p>
      By adhering to the OSCOLA referencing style, you create a bridge between
      your arguments and the legal authorities that support them. This enhances
      the coherence and reliability of your work and facilitates the
      readers&apos; ability to engage with and assess your legal reasoning.
      Whether you are a law student, an academic, or a legal professional,
      understanding and applying the OSCOLA style is a crucial skill in crafting
      persuasive and credible legal writing.
    </p>
  </GuideSection>
);

const FormatFootnotes: React.FC = () => (
  <GuideSection id="Footnoes">
    <GuideHeader>Format OSCOLA Footnotes</GuideHeader>
    <p>
      In the OSCOLA Referencing Style, footnotes serve as brief references at
      the bottom of the page, leading readers to the full citation in the
      bibliography. These references typically include the title of the case or
      statute, the year of publication, and specific details such as page
      numbers.
    </p>
    <p>
      The following examples illustrate how to cite various legal documents
      using the OSCOLA style:
    </p>
    <p>
      <strong>Legal cases:</strong> The primary method for citing cases includes
      the parties&apos; names, year, and report citation. For example:{' '}
      <IntextCitation>
        R (Miller) v Secretary of State for Exiting the European Union [2017]
        UKSC 5.
      </IntextCitation>
    </p>
    <p>
      <strong>Legislation:</strong> For statutes, use the short title and the
      year in italics. For example:{' '}
      <IntextCitation>Equality Act 2010.</IntextCitation>
    </p>
    <p>
      <strong>Secondary sources - Books:</strong> For books, include the
      author&apos;s initials, last name, title in italics, edition if relevant,
      (publisher, year), and specific pages. For example:{' '}
      <IntextCitation>
        J M Finnis, Natural Law and Natural Rights (2nd edn, OUP 2011) 275.
      </IntextCitation>
    </p>
    <p>
      <strong>Secondary sources - Articles:</strong> For journal articles, use
      the author&apos;s initials, last name, title in single quotes, [year],
      volume (issue number), journal name or abbreviation, and first page of the
      article. For example:{' '}
      <IntextCitation>
        P Craig, &apos;Theory and Values in Public Law&apos; [2004] PL 598.
      </IntextCitation>
    </p>
    <p>
      <strong>Multiple citations in one footnote:</strong> If referencing
      multiple sources in one footnote, separate each citation with a semicolon.
      For example:{' '}
      <IntextCitation>
        Equality Act 2010, s 15(1)(a); R (Miller) [2017] UKSC 5, para 57.
      </IntextCitation>
    </p>
    <p>
      <strong>Electronic sources:</strong> Include the author&apos;s name, title
      in italics, web address, and accessed date for online materials. For
      example:{' '}
      <IntextCitation>
        T Bingham, &apos;The Rule of Law&apos; (The Rule of Law, 2007)
        &lt;www.cps.org.uk accessed 10 March 2022&gt;.
      </IntextCitation>
    </p>
    <p>
      <strong>Extended quotations:</strong> In OSCOLA, it is common to place a
      lengthy quotation within the footnotes rather than the main body text.
    </p>
    <p>
      <strong>Example:</strong> M Lord Neuberger emphasizes the importance of
      the rule of law, stating: &apos;Adherence to the rule of law informs every
      aspect of the judicial duty, including, of course, the determination,
      interpretation, and application of the law.&apos;^1
    </p>
    <p>
      <IntextCitation>
        ^1 R (Miller) v Secretary of State for Exiting the EU [2017] UKSC 5,
        para 40.
      </IntextCitation>
    </p>
    <p>
      The OSCOLA referencing style ensures a standardized method of citation
      within the field of law. By carefully following these rules, you can
      construct a precise and professional legal document that appropriately
      credits the works and authorities that have informed your arguments.
    </p>
  </GuideSection>
);

const Bibliography: React.FC = () => (
  <GuideSection id="Bibliography">
    <GuideHeader>Generate Bibliography in OSCOLA</GuideHeader>
    <p>
      The OSCOLA citation style, used predominantly in legal studies, follows a
      distinct approach that often involves footnotes for citations and may also
      include a bibliography. Here&apos;s how to format a bibliography in the
      OSCOLA style:
    </p>
    <p>
      <strong>Title and placement:</strong> The bibliography should begin on a
      new page at the end of your work. Center the heading
      &quot;Bibliography&quot; (without quotation marks) at the top of the page.
      Do not bold, italicize, or underline the heading.
    </p>
    <p>
      <strong>Spacing and margins:</strong> Employ single-spacing within entries
      and double-spacing between them in the bibliography. Keep 1-inch margins
      on all sides of the page. Unlike the Harvard style, OSCOLA does not use
      hanging indents; instead, the first line of each citation should align
      with the left margin.
    </p>
    <p>
      <strong>Organize your citations:</strong> OSCOLA recommends separating
      primary sources such as cases and legislation from secondary sources like
      books and articles. Within these categories, you should alphabetize the
      list by the first significant word of the citation.
    </p>
    <p>
      <strong>Pay attention to punctuation and italics:</strong> Use italics for
      the titles of books, reports, and legislation. Quotation marks are used
      for article titles. Ensure that punctuation marks are consistently applied
      following the OSCOLA guidelines.
    </p>
    <p>
      <strong>Check for accuracy:</strong> Ensure that each reference is
      accurate, including the correct use of italics, capitalization, and other
      formatting as per OSCOLA standards.
    </p>
    <img
      src={require('images/oscola-reference-list.png')}
      alt="Screenshot of an OSCOLA reference list"
    />
    <p>
      By adhering to these rules, you can create a bibliography that aligns with
      the specific requirements of the OSCOLA citation style, helping your
      readers easily navigate and verify the legal authorities cited in your
      work.
    </p>
  </GuideSection>
);

const BookCitations: React.FC = () => (
  <GuideSection id="Books">
    <GuideHeader>Cite Books in OSCOLA</GuideHeader>
    <p>
      Law is built upon centuries of tradition, so it&apos;s only fitting that
      the citation of legal documents should have its own unique set of rules.
      Enter OSCOLA, a standardized citation system that brings order to the
      potentially chaotic world of legal citation. Let&apos;s take a closer look
      at how to reference books in OSCOLA, with examples and some surprising
      exceptions.
    </p>
    <CitationBlock header="Citing a book in OSCOLA">
      Author&apos;s Last Name First Initials, Title of Book (Edition Number edn,
      Publisher Name Year Published)
    </CitationBlock>
    <p>
      <strong>For Example:</strong>
    </p>
    <CitationExampleBox>
      HLA Hart, The Concept of Law (3rd edn, OUP 2012)
    </CitationExampleBox>
    <p>
      <strong>Multiple authors.</strong> If the book has multiple authors, list
      them in the order they appear in the book, using &quot;and&quot; between
      authors:
    </p>
    <CitationExampleBox>
      Andrew Ashworth and Jeremy Horder, Principles of Criminal Law (7th edn,
      OUP 2013)
    </CitationExampleBox>
    <p>
      <strong>Chapter in an edited book.</strong> For a chapter in an edited
      book, use the author&apos;s name of the chapter, the chapter title, the
      editor&apos;s initials and last name, the book title, and publication
      details:
    </p>
    <CitationExampleBox>
      Thomas Hobbes, &quot;Of the Natural Condition of Mankind&quot; in J Curley
      (ed), Leviathan (1st edn, Hackett 1994)
    </CitationExampleBox>
    <p>
      <strong>No author.</strong> In cases where the author of the book is not
      known, the title of the book is used instead, followed by the
      publisher&apos;s name and year of publication:
    </p>
    <CitationExampleBox>
      Guide to Law and Legal Procedures (Law Society 2006)
    </CitationExampleBox>
    <p>
      <strong>Citing multiple volumes:</strong> When citing a multi-volume work,
      include the volume number after the year of publication.
    </p>
    <CitationExampleBox>
      Thomas Blackstone, Commentaries on the Laws of England (OUP 1769) vol 2
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Reference List Order</strong>
      </p>
    </LargeText>
    <p>
      In OSCOLA, your reference list must be sorted by author&apos;s surname and
      then chronologically by publication date.
    </p>
    <p>
      OSCOLA citation brings consistency and professionalism to legal writing.
      The guidance provided above for citing books, from single authors to
      multiple volumes, ensures you have the tools to cite correctly.
    </p>
    <p>
      As you engage in the practice of law, remember that proper citation not
      only follows rules but acknowledges the intellectual heritage of your
      work, connecting you to the broader legal conversation.
    </p>
  </GuideSection>
);

const JournalCitations: React.FC = () => (
  <GuideSection id="Journal">
    <GuideHeader>Cite Journal Articles in OSCOLA</GuideHeader>
    <p>
      Journal articles, as valuable sources of up-to-date research and
      commentary, form an integral part of legal scholarship. Referencing them
      correctly is crucial in legal writing. The OSCOLA style, devised by Oxford
      University, gives us a structured, professional, and widely-accepted
      method to do this.
    </p>
    <CitationBlock header="Citing a journal article in OSCOLA">
      Author&apos;s Last Name First Initials, &apos;Title of Article&apos;
      (Year) Volume Number Abbreviated Journal Name First Page of Article
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      S Bright, &apos;The Lease as a Modifiable Contract&apos; (2006) 69 Conv
      526
    </CitationExampleBox>
    <p>
      <strong>Journal articles available online.</strong> If the journal is only
      available electronically or is paginated separately to a print version,
      the citation should include [online], the volume number and the web
      address where the article can be found:
    </p>
    <CitationExampleBox>
      S Bright, &apos;The Lease as a Modifiable Contract&apos; (2006) 69 Conv
      [online] www.jstor.org/stable/conv69
    </CitationExampleBox>
    <p>
      <strong>No volume number.</strong> If a journal doesn&apos;t have volume
      numbers, you should use the issue number instead, after the year:
    </p>
    <CitationExampleBox>
      S Bright, &apos;The Lease as a Modifiable Contract&apos; (2006) Issue 4
      Conv 526
    </CitationExampleBox>
    <p>
      <strong>No page numbers.</strong> Some online journals do not have page
      numbers. In such cases, you can omit the page numbers:
    </p>
    <CitationExampleBox>
      S Bright, &apos;The Lease as a Modifiable Contract&apos; (2006) 69 Conv
      [online]
    </CitationExampleBox>
    <p>
      <strong>Article from an online database.</strong> If you accessed an
      article from a web database, you should cite the database name, the
      document number, and the URL:
    </p>
    <CitationExampleBox>
      S Bright, &apos;The Lease as a Modifiable Contract&apos; (2006) 69 Conv
      [JSTOR]
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Using Footnotes</strong>
      </p>
    </LargeText>
    <p>
      In OSCOLA, in-text citations are replaced by footnotes. Each time you
      reference a source, you should provide a footnote with the full citation
      details. This applies to all references, including those to journal
      articles.
    </p>
    <p>
      Referencing journal articles accurately in OSCOLA style adds credibility
      and professionalism to your writing. Whether your sources are
      single-author, multi-author, anonymous, or electronic, the guidance
      provided above ensures you have the tools to cite correctly.
    </p>
  </GuideSection>
);

const WebsiteCitations: React.FC = () => (
  <GuideSection id="Website">
    <GuideHeader>Cite Websites in OSCOLA</GuideHeader>
    <p>
      Websites offer a plethora of information, both official and unofficial,
      that can be pertinent to legal studies. Correctly referencing this online
      material, in compliance with OSCOLA guidelines, ensures your work
      maintains its integrity. While it might feel like traversing a new
      frontier, citing web sources doesn&apos;t have to be daunting.
    </p>
    <CitationBlock header="Citing a website in OSCOLA">
      Author(s), &apos;Title of the Web Document/Page&apos; (Full Date of
      Creation or Last Update) accessed Day Month Year
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Bright S, &apos;Land Law&apos; (18 May 2023) http://www.landlaw.co.uk
      accessed 20 May 2023
    </CitationExampleBox>
    <p>
      <strong>Website with corporate author.</strong> If the website has a
      corporate author, the citation should begin with the corporate
      entity&apos;s name, followed by the title of the webpage, the full date
      the page was last updated, and the web address:
    </p>
    <CitationExampleBox>
      Land Registry, &apos;Title Register&apos; (18 May 2023)
      http://www.landregistry.co.uk accessed 20 May 2023
    </CitationExampleBox>
    <p>
      <strong>No Known Author.</strong> In instances where the author of a
      webpage is not known, start with the title of the webpage, followed by the
      full date the page was last updated, and the web address:
    </p>
    <CitationExampleBox>
      &apos;Land and Property Law&apos; (18 May 2023) http://www.landlaw.co.uk
      accessed 20 May 2023
    </CitationExampleBox>
    <p>
      <strong>Blogs or online articles.</strong> Blogs or online articles follow
      the same general structure, with the addition of the name of the blog or
      the publication in italics after the author&apos;s name:
    </p>
    <CitationExampleBox>
      Smith J, &apos;Land Law Principles&apos; (18 May 2023) The Law Journal
      Blog www.thelawjournalblog.co.uk/landlawprinciples accessed 20 May 2023
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Access Dates</strong>
      </p>
    </LargeText>
    <p>
      Remember to always include the access date when citing web sources, as
      online content can change over time. The access date serves to let readers
      know when you last verified the information on the webpage.
    </p>
    <p>
      While it may seem intimidating at first, referencing websites in OSCOLA
      style can be a straightforward process when following these guidelines.
      Whether your sources are webpages, corporate websites, or blogs, you are
      now equipped with the tools necessary for accurate citation.
    </p>
  </GuideSection>
);

const BookChapterCitations: React.FC = () => (
  <GuideSection id="BookChapter">
    <GuideHeader>Cite Book Chapters in OSCOLA</GuideHeader>
    <p>
      In legal scholarship, book chapters often provide in-depth coverage of a
      specific topic, making them an indispensable resource. When it comes to
      citing these chapters using the OSCOLA referencing style, precision is
      key. Let&apos;s walk you through the citation process.
    </p>
    <CitationBlock header="Citing a book chapter in OSCOLA">
      Author&apos;s Last Name First Initials, &apos;Title of Chapter&apos; in
      Book Title (eds) (Publisher, Year) First Page of Chapter
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Jones A, &apos;The Law of Property&apos; in The Laws of England (eds)
      (OUP, 2023) 121
    </CitationExampleBox>
    <p>
      <strong>Book chapter with multiple authors.</strong> When citing a book
      chapter written by multiple authors, list the authors as they appear in
      the chapter, using &apos;and&apos; between the authors:
    </p>
    <CitationExampleBox>
      Smith J and Jones A, &apos;The Law of Property&apos; in The Laws of
      England (eds) (OUP, 2023) 121
    </CitationExampleBox>
    <p>
      <strong>No author:</strong> In cases where the author of the chapter is
      not mentioned, start with the title of the chapter, followed by the title
      of the book, editors of the book, and the first page of the chapter.
    </p>
    <CitationExampleBox>
      &apos;The Law of Property&apos; in The Laws of England (eds) (OUP, 2023)
      121
    </CitationExampleBox>
    <p>
      <strong>Book with single author, citing specific chapter:</strong> When
      citing a specific chapter of a book written by a single author, mention
      chapter details after the book&apos;s details.
    </p>
    <CitationExampleBox>
      Jones A, The Laws of England (OUP, 2023) 121
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: The Pinpoint Citation</strong>
      </p>
    </LargeText>
    <p>
      In OSCOLA, &apos;pinpoint citations&apos; allow you to direct the reader
      to a specific page or pages where the cited information can be found. The
      first page of the chapter, followed by the specific page number, should be
      provided at the end of the citation (e.g., 121-122).
    </p>
    <p>
      Remember, citing book chapters accurately in OSCOLA style is crucial for
      acknowledging the work of authors and editors, and for guiding your
      readers to your sources. Whether your sources are single-author,
      multi-author, or anonymous, this guide has provided you with the know-how
      to tackle your citations with confidence.
    </p>
  </GuideSection>
);

const NewspaperMagazineCitations: React.FC = () => (
  <GuideSection id="NewswpaperMagazine">
    <GuideHeader>Cite Newspapers & Magazines in OSCOLA</GuideHeader>
    <p>
      Conference papers are vital sources of latest research findings and
      theories before they make it to formal publications. When referencing
      these sources in legal studies and scholarly works, the OSCOLA referencing
      style provides a professional, structured and well-accepted format to do
      so.
    </p>
    <CitationBlock header="Citing a website in OSCOLA">
      Author&apos;s Last Name First Initials, &apos;Title of Article&apos;
      Newspaper or Magazine Title (Full Date)
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      James S, &apos;Brexit and the Law: An Analysis&apos; The Guardian (30 June
      2023)
    </CitationExampleBox>
    <p>
      <strong>Online articles.</strong> For online articles, include the web
      address (URL) at the end of the citation, and the access date if no
      publication date is available:
    </p>
    <CitationExampleBox>
      James S, &apos;Brexit and the Law: An Analysis&apos; The Guardian (30 June
      2023) &lt;www.theguardian.com&gt; accessed 1 July 2023
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Secondary Referencing</strong>
      </p>
    </LargeText>
    <p>
      In OSCOLA, secondary referencing (citing a source you haven&apos;t read
      yourself, but which is cited in a source you have read) is generally
      discouraged. It&apos;s always better to read the original source for
      yourself. However, if the original is not available, make it clear you
      have not read the original source by acknowledging the secondary source in
      your footnote.
    </p>
    <p>
      Citing newspaper and magazine articles accurately in OSCOLA style enhances
      the diversity of your legal arguments, demonstrates the breadth of your
      research, and respects the journalistic craft.
    </p>
  </GuideSection>
);

const ConferencePaperCitations: React.FC = () => (
  <GuideSection id="ConferencePaper">
    <GuideHeader>Cite Conference Papers in OSCOLA</GuideHeader>
    <p>
      Conference papers are vital sources of latest research findings and
      theories before they make it to formal publications. When referencing
      these sources in legal studies and scholarly works, the OSCOLA referencing
      style provides a professional, structured and well-accepted format to do
      so.
    </p>
    <CitationBlock header="Citing a conference paper in OSCOLA">
      Author&apos;s Last Name First Initials, &apos;Title of Paper&apos;, Title
      of Conference (Conference Date) Page Number
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Smith J, &apos;Legal Structures in Modern Society&apos;, The Society of
      Legal Scholars Annual Conference (3 September 2023) 23
    </CitationExampleBox>
    <p>
      <strong>Multiple authors:</strong> If the paper has multiple authors, list
      them in the order they appear in the paper, using &apos;and&apos; between
      authors.
    </p>
    <CitationExampleBox>
      Jones S and Brown K, &apos;Property Law and Its Implications&apos;,
      Property Law Conference (12 March 2023) 47
    </CitationExampleBox>
    <p>
      <strong>Online conference papers:</strong> If the conference paper is
      accessed online, the citation should include the web address or DOI (if
      available) at the end.
    </p>
    <CitationExampleBox>
      Davis G, &apos;Tackling Cybercrime&apos;, Cyber Law Symposium (2 February
      2023) 102 &lt;www.cyberlawsymposium.com&gt;
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Always in Footnotes</strong>
      </p>
    </LargeText>
    <p>
      OSCOLA uses footnotes for citations. Each time you reference a source, you
      should provide a footnote with the full citation details. This applies to
      all references, including those to conference papers.
    </p>
    <p>
      Citing conference papers correctly in OSCOLA style not only provides your
      work with accurate references, but also adds an element of professionalism
      to your writing. So, whether you&apos;re citing single-author,
      multi-author, anonymous, or electronic papers, our citation generator
      equips you to do so effectively.
    </p>
  </GuideSection>
);

const CaseNoteCitations: React.FC = () => (
  <GuideSection id="CaseNotes">
    <GuideHeader>Cite Case Notes in OSCOLA</GuideHeader>
    <p>
      Case notes, or case comments, can be a goldmine of legal analysis,
      elucidating the nuances of a court&apos;s decision and its implications on
      existing law. When you&apos;re writing a legal essay, thesis or article,
      citing these valuable sources accurately in OSCOLA style is crucial to
      creating a robust piece of legal scholarship.
    </p>
    <p>
      <strong>Case notes: </strong>
      When citing a case note, begin with the author&apos;s name, the title of
      the note in single quotes, the year of the case note, the volume number,
      the abbreviated name of the law report, and the page number.
    </p>
    <CitationBlockMini>
      Author&apos;s Last Name First Initials, &apos;Title of Case Note&apos;
      (Year) Volume Number Abbreviated Report Name Page
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Green A, &apos;Reconsidering R v Ireland: The Marital Coercion
      Defence&apos; [2013] Crim LR 10
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Citing Multiple Authors</strong>
      </p>
    </LargeText>
    <p>
      In the event that the case note has multiple authors, list them in the
      order they appear in the article, using &apos;and&apos; between authors.
      For example, Smith A and Brown B, &apos;Title of Case Note&apos; [Year]
      Abbreviated Report Name Page.
    </p>
  </GuideSection>
);
const InterviewCitations: React.FC = () => (
  <GuideSection id="Interview">
    <GuideHeader>Cite Interviews in OSCOLA</GuideHeader>
    <p>
      Interviews can provide a treasure trove of insights, especially when
      researching a contentious legal issue or trying to glean a
      practitioner&apos;s viewpoint. In legal scholarship, whether these
      interviews are conducted by you or you are citing an interview conducted
      by another, they should be accurately referenced in OSCOLA.
    </p>
    <p>
      <strong>Citing published interviews:</strong> For published interviews,
      start with the name of the interviewee, followed by the title of the
      interview in single quotes, the name of the publication in italics, and
      the date.
    </p>
    <CitationBlockMini>
      Interviewee&apos;s Last Name First Initials, &apos;Title of
      Interview&apos;, Publication Name (Date)
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      D Cameron, &apos;Brexit: The Interview&apos;, The Guardian (London, 2 June
      2016)
    </CitationExampleBox>
    <p>
      <strong>Citing unpublished interviews:</strong> For unpublished interviews
      that you conducted, include the name of the interviewee, the format (eg,
      interview), the location of the interview, and the date.
    </p>
    <CitationBlockMini>
      Interviewee&apos;s Last Name First Initials, Interview, Location (Date)
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Smith J, Interview, Oxford (3 March 2023)
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Using First-Person</strong>
      </p>
    </LargeText>
    <p>
      OSCOLA referencing style does not shy away from the first person. So when
      you&apos;re citing an interview that you&apos;ve conducted, you can use
      &apos;I&apos; in your footnote. For instance, &apos;In an interview, I
      conducted with Justice Smith on 3 March 2023 in Oxford…&apos;.
    </p>
  </GuideSection>
);
const EncyclopediaCitations: React.FC = () => (
  <GuideSection id="Encyclopedia">
    <GuideHeader>Cite Encyclopedias in OSCOLA</GuideHeader>
    <p>
      Legal encyclopedias are a cornerstone of comprehensive legal research,
      providing a digest of law that is written in a narrative form. These
      resources come in handy when you need a concise, accurate and up-to-date
      overview of a legal topic. Referencing these properly, thus, is a critical
      skill in crafting sound legal arguments.
    </p>
    <p>
      <strong>Citing general legal encyclopedias:</strong> When citing from a
      the title of the encyclopedia should be in italics, followed by the
      edition, the volume number, the title of the entry in single quotes, and
      the paragraph or page number.
    </p>
    <CitationBlockMini>
      Title of Encyclopedia (edition) volume &apos;Title of Entry&apos;
      paragraph/page number
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Halsbury&apos;s Laws of England (5th edn) vol 30 &apos;Council Tax&apos;
      para 680
    </CitationExampleBox>
    <p>
      <strong>Citing a specific entry:</strong> When citing a specific entry
      within an encyclopedia, you&apos;ll still need to include the title of the
      encyclopedia, the edition, volume, title of the entry, but you also
      include a pinpoint reference to the relevant paragraph or page number.
    </p>
    <CitationBlockMini>
      Title of Encyclopedia (edition) volume &apos;Title of Entry&apos; pinpoint
      reference
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Halsbury&apos;s Laws of England (5th edn) vol 30 &apos;Council Tax&apos;,
      para 15
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Making Use of Legal Encyclopedias</strong>
      </p>
    </LargeText>
    <p>
      Legal encyclopedias, like Halsbury&apos;s Laws of England, are a great
      starting point for any legal research. These resources provide a
      high-level overview of legal topics and can help guide your further
      research by pointing you to relevant cases, legislation, and secondary
      sources. As always, ensure to reference these resources accurately when
      used.
    </p>
  </GuideSection>
);
const HansardCitations: React.FC = () => (
  <GuideSection id="Hansard">
    <GuideHeader>Cite Hansard in OSCOLA</GuideHeader>
    <p>
      Hansard is the traditional name for the transcripts of Parliamentary
      debates and is an invaluable resource for anyone researching the
      legislative history of a particular act, or understanding the policy
      debates of a certain era. As such, it is a source that often needs to be
      cited in legal documents.
    </p>
    <p>
      <strong>Citing a debate:</strong> If you&apos;re citing a debate, start
      with the house (either HL Deb or HC Deb for the House of Lords or House of
      Commons respectively), followed by the date (in square brackets), the
      volume, &apos;col&apos; and the column number.
    </p>
    <CitationBlockMini>
      HL Deb or HC Deb [Date] Volume col Column Number
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>HL Deb [2005] vol 667 cols 681-82</CitationExampleBox>
    <p>
      <strong>Citing a written answer:</strong> If you&apos;re citing a written
      answer, the citation structure remains the same, but the house will be
      either HL WA or HC WA.
    </p>
    <CitationBlockMini>
      HL WA or HC WA [Date] Volume col Column Number
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>Donoghue v Stevenson [1932] AC 562</CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Using Online Resources</strong>
      </p>
    </LargeText>
    <p>
      Since 2005, Hansard has been available online and the website offers an
      easy way to access both recent debates and historic ones. It&apos;s a good
      idea to hyperlink to the online version of Hansard if you are working on
      an electronic document, allowing your readers to access the full text of
      the debate you are citing. Be sure to check the URL for permanency if you
      intend to do this.
    </p>
  </GuideSection>
);
const OtherJurisdictionCitations: React.FC = () => (
  <GuideSection id="OtherJuristictions">
    <GuideHeader>Cite Cases from Other Jurisdictions in OSCOLA</GuideHeader>
    <p>
      Globalization has made it increasingly common for legal scholars and
      practitioners to refer to case law from jurisdictions outside their own.
      The OSCOLA referencing style offers a framework for citing these cases,
      enabling a clear and uniform approach to international legal citation.
    </p>
    <p>
      <strong>USA cases:</strong> When citing cases from the United States,
      provide the name of the case, the reporter volume number, the abbreviated
      name of the reporter, the first page of the report, and the year in
      brackets.
    </p>
    <CitationBlockMini>
      Case Name Volume Reporter First Page (Year)
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>Roe v Wade 410 US 113 (1973)</CitationExampleBox>
    <p>
      <strong>Australia and Canada Cases:</strong> Australian and Canadian cases
      should be cited by the case name, the year of the report in square
      brackets, the volume number (if available), the report abbreviation, and
      the first page.
    </p>
    <CitationBlockMini>
      Case Name [Year] Volume Report Abbreviation First Page
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>Donoghue v Stevenson [1932] AC 562</CitationExampleBox>
    <p>
      <strong>European Court of Human Rights (ECHR) cases:</strong> For ECHR
      cases, start with the case name, followed by the application number, the
      court name, and the date of the decision.
    </p>
    <CitationBlockMini>Case Name App No Court Name (Date)</CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Soering v United Kingdom App no 14038/88 (ECtHR, 7 July 1989)
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Adding Context</strong>
      </p>
    </LargeText>
    <p>
      When citing cases from jurisdictions with which your reader might be
      unfamiliar, it can be helpful to provide a bit of context, either in the
      text of your paper or in a footnote. This might include the legal issue at
      stake, the jurisdiction&apos;s legal tradition, or the significance of the
      case in that jurisdiction&apos;s law.
    </p>
  </GuideSection>
);
const EULegalSourceCitations: React.FC = () => (
  <GuideSection id="EULegalSource">
    <GuideHeader>Cite EU Legal Sources in OSCOLA</GuideHeader>
    <p>
      The European Union&apos;s legal system is one of the most intricate in the
      world, generating a vast array of legislation, case law, and official
      documents. The OSCOLA referencing style provides a standardised method for
      citing these diverse sources in academic work, maintaining clarity and
      precision in legal citation.
    </p>
    <p>
      <strong>Treaties:</strong> For EU treaties, begin with the name of the
      treaty in italics, followed by the year of signing and the OJ reference in
      brackets.
    </p>
    <CitationBlockMini>
      Name of Treaty (signed year, entered into force year) OJ Series
      Issue/First Page
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Treaty on the Functioning of the European Union (signed 2007, entered into
      force 2009) OJ C115/47
    </CitationExampleBox>
    <p>
      <strong>Regulations and Directives:</strong> EU regulations and directives
      are cited with the type of instrument and number, title, and OJ reference.
    </p>
    <CitationBlockMini>
      Type of Instrument No/Year Title [Year] OJ Series Issue/First Page
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Regulation (EU) 2016/679 General Data Protection Regulation [2016] OJ
      L119/1
    </CitationExampleBox>
    <p>
      <strong>Communications and reports:</strong> Communications from the
      European Commission and other EU bodies, as well as reports, are cited
      similarly to EU legislation, but may not always have an OJ reference.
    </p>
    <CitationBlockMini>
      Name of Institution, Title of Document (additional info, Document No)
      [Year]
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      European Commission, A Digital Single Market Strategy for Europe
      (COM(2015) 192 final) [2015]
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Getting the Details Right</strong>
      </p>
    </LargeText>
    <p>
      OSCOLA provides detailed instructions for referencing EU legal materials,
      including guidelines for citing materials in languages other than English.
      Always refer to the most recent version of the OSCOLA guide to ensure
      you&apos;re following the correct format.
    </p>
  </GuideSection>
);
const PrimaryLegislationCitations: React.FC = () => (
  <GuideSection id="PrimaryLegislation">
    <GuideHeader>Cite Primary Legislation in OSCOLA</GuideHeader>
    <p>
      Primary legislation refers to laws or statutes that have been enacted by a
      legislative body such as Parliament in the UK. This includes Acts of
      Parliament and Bills, which are often integral to legal arguments and
      discussions. When citing these sources in your work, you should use the
      OSCOLA referencing style, which provides clear guidelines for accurate
      citation.
    </p>
    <p>
      <strong>Acts of Parliament:</strong> When citing an Act of Parliament, you
      should give the short title (in italics), followed by the chapter number.
    </p>
    <CitationBlockMini>Short Title Year (c Chapter Number)</CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>Human Rights Act 1998 (c 42)</CitationExampleBox>

    <p>
      <strong>Bills:</strong> For a Bill, cite the short title (in italics),
      followed by the type of Bill and the parliamentary session in brackets.
    </p>
    <CitationBlockMini>Short Title Type of Bill (Session)</CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Climate Change and Wildlife Bill HL Bill (2018-19 [HL])
    </CitationExampleBox>

    <p>
      <strong>Pre-1963 Acts:</strong> If you are citing an Act that was passed
      before 1963, the year it was enacted should be included in roman numerals
      in the title.
    </p>
    <CitationBlockMini>
      Case Name (Year) Volume Report Abbreviation First Page
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Short Title (Regnal Year of Monarch) c Chapter Number
    </CitationExampleBox>
    <p>
      <strong>Amended or repealed Acts:</strong> If an Act has been
      significantly amended or repealed, you should indicate this in your
      citation.
    </p>
    <CitationBlockMini>
      Short Title Year (c Chapter Number; repealed by Short Title Year (c
      Chapter Number))
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Mental Health Act 1983 (c 20; repealed by Mental Health Act 2007 (c 12))
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Referenceing Secondary Legislation</strong>
      </p>
    </LargeText>
    <p>
      Secondary legislation, such as Statutory Instruments or Orders in Council,
      are typically cited similarly to Acts but include the SI number. For
      example, The Animals (Scientific Procedures) Act 1986 Amendment
      Regulations 2012 SI 2012/3039. Always refer to OSCOLA guidelines to ensure
      accurate citation of different types of legislation.
    </p>
  </GuideSection>
);
const LegalCaseCitations: React.FC = () => (
  <GuideSection id="LegalCases">
    <GuideHeader>Cite Legal Cases in OSCOLA</GuideHeader>
    <p>
      Legal cases form the backbone of law and legal studies. They set
      precedents, create laws, and provide explanations for complicated legal
      concepts. When writing legal essays, assignments, or dissertations, citing
      these cases accurately using the OSCOLA referencing style adds
      authenticity to your work.
    </p>
    <p>
      <strong>UK cases: </strong>When citing a UK case, you should start with
      the case name (in italics), followed by the neutral citation (if there is
      one), and then the volume number, the abbreviated name of the report, and
      the first page of the report.
    </p>
    <CitationBlockMini>
      Case Name [Year] Court Abbreviation Case Number, [Year] Volume Report
      Abbreviation First Page
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Corr v IBC Vehicles Ltd [2008] UKHL 13, [2008] 1 AC 884
    </CitationExampleBox>
    <p>
      <strong>EU Cases: </strong>For EU cases, cite the case name (in italics),
      then the case number and the report citation.
    </p>
    <CitationBlockMini>
      Case Name Case Number, Report Citation
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Pfeiffer and Others C-397/01 to C-403/01, [2005] ECR I-8835
    </CitationExampleBox>
    <p>
      <strong>No neutral citation:</strong> If there&apos;s no neutral citation
      for a UK case, start with the case name, followed by the volume number,
      the abbreviated name of the report, and the first page of the report.
    </p>
    <CitationBlockMini>
      Case Name (Year) Volume Report Abbreviation First Page
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      R v Secretary of State for the Home Department, Ex p Fire Brigades&apos;
      Union [1995] 2 AC 513
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Law Report Hierarchy</strong>
      </p>
    </LargeText>
    <p>
      When citing UK cases, OSCOLA has a hierarchy of law reports. The &apos;Law
      Reports&apos; (Appeal Cases (AC), Queen&apos;s Bench Division (QB),
      Chancery Division (Ch), and Family Division (Fam)) take precedence. If a
      case isn&apos;t reported in the Law Reports, use the Weekly Law Reports
      (WLR) or the All England Reports (All ER). Always check your university or
      institution&apos;s guidelines as preferences can vary.
    </p>
  </GuideSection>
);
const SocialMediaCitations: React.FC = () => (
  <GuideSection id="SocialMedia">
    <GuideHeader>Cite Social Media Posts in OSCOLA</GuideHeader>
    <p>
      As the digital age evolves, so does our approach to sourcing information.
      Social media posts are increasingly being used as sources of information
      in academic and legal writing. OSCOLA referencing style provides us with a
      clear and professional way of citing these unconventional sources.
    </p>
    <p>
      <strong>Citing Twitter posts:</strong> When citing a tweet, you should
      start with the author&apos;s Twitter handle, followed by the text of the
      tweet in single quotation marks, then &apos;Twitter&apos; and the date of
      the tweet.
    </p>
    <CitationExampleBox>
      @BarristerSecret, &apos;Unreliable Narrator warning. Everything you are
      about to read may be inaccurate (Twitter, 1 March 2020)
    </CitationExampleBox>
    <p>
      <strong>Citing Facebook posts:</strong> For Facebook posts, begin with the
      individual or group authoring the post, followed by (Facebook) and the
      precise date of the post.
    </p>
    <CitationExampleBox>
      Pepper, S, &apos;Legal Formalism and Legal Realism: What is the
      Issue?&apos;, Oxford Law Faculty Blogs (20 July 2020)
    </CitationExampleBox>
    <p>
      <strong>Citing blog posts:</strong> To cite a blog post, include the
      author&apos;s name, the title of the blog post in single quotes, the name
      of the blog, and the date of the blog post.
    </p>
    <CitationExampleBox>
      Richards J, &apos;Comparative Analysis of Property Laws&apos; (PhD thesis,
      University of Oxford 2022) &lt;www.oxford.edu/theses/8630&gt;
    </CitationExampleBox>
    <p>
      <strong>Citing Instagram posts:</strong> For Instagram posts, cite as you
      would a blog post, but include &apos;Instagram post&apos; at the end of
      the citation.
    </p>
    <CitationExampleBox>
      @legalcheek, &apos;Bar exam pass rate falls to 58%&apos;, (Instagram Post,
      July 2020)
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Capture the Moment</strong>
      </p>
    </LargeText>
    <p>
      Due to the transient nature of social media posts, it&apos;s important to
      capture a screenshot or download a copy of the posts you cite. This will
      ensure you have a record in case the post is deleted or edited in future.
      This evidence may be necessary for submitting your work, depending on your
      institution&apos;s guidelines.
    </p>
  </GuideSection>
);

const ThesisDissertationCitations: React.FC = () => (
  <GuideSection id="ThesisDissertation">
    <GuideHeader>Cite Theses &amp; Dissertations in OSCOLA</GuideHeader>
    <p>
      Theses and dissertations serve as rich sources of specialised research and
      novel insights. In law and other scholarly works, the OSCOLA referencing
      style offers a well-structured and professionally accepted means of citing
      these sources. Let&apos;s dive in and explore how to do this effectively.
    </p>
    <CitationBlock header="Citing a thesis or dissertation in OSCOLA">
      Author&apos;s Last Name First Initials, &apos;Title of Thesis&apos;
      (Degree, Institution Year)
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Richards J, &apos;Comparative Analysis of Property Laws&apos; (PhD thesis,
      University of Oxford 2022)
    </CitationExampleBox>
    <p>
      <strong>Multiple authors:</strong> If the paper has multiple authors, list
      them in the order they appear in the paper, using &apos;and&apos; between
      authors.
    </p>
    <CitationExampleBox>
      Jones S and Brown K, &apos;Property Law and Its Implications&apos;,
      Property Law Conference (12 March 2023) 47
    </CitationExampleBox>
    <p>
      <strong>Online theses or dissertations.</strong> If the thesis or
      dissertation is accessed online, the citation should include the URL or
      DOI (if available) at the end:
    </p>
    <CitationExampleBox>
      Richards J, &apos;Comparative Analysis of Property Laws&apos; (PhD thesis,
      University of Oxford 2022) &lt;www.oxford.edu/theses/8630&gt;
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Unique Identifiers for Easy Access</strong>
      </p>
    </LargeText>
    <p>
      Consider including unique identifiers such as DOIs (Digital Object
      Identifier) or a URL in your citation whenever they are available,
      especially for online theses or dissertations. They help readers to
      directly access the source and make your work more credible and
      reproducible. Remember, a well-cited paper reflects your thorough research
      and respect for original authors&apos; work.
    </p>
  </GuideSection>
);

const PersonalCommunicationCitations: React.FC = () => (
  <GuideSection id="PersonalCommunication">
    <GuideHeader>Cite Personal Communications in OSCOLA</GuideHeader>
    <p>
      Personal communications can offer unique insights and perspectives that
      enrich your legal argument. They may come in the form of emails,
      interviews, conversations, letters, or similar. Given their value,
      it&apos;s important to know how to reference them appropriately in your
      legal writing using OSCOLA style.
    </p>
    <p>
      <strong>Personal communication:</strong> When citing a personal
      communication, begin with the name of the person from whom the
      communication originated, followed by (in brackets) the description of the
      communication and the date.
    </p>
    <CitationBlockMini>
      Author&apos;s Last Name First Initials (Description of communication,
      Date)
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      B Smith (Letter to the author, 15 July 2021)
    </CitationExampleBox>
    <p>
      <strong>Email:</strong> For emails, the structure remains the same, except
      the description of the communication would be &apos;Email to the
      author&apos;.
    </p>
    <CitationBlockMini>
      Author&apos;s Last Name First Initials (Email to the author, Date)
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      J Doe (Email to the author, 6 December 2022)
    </CitationExampleBox>
    <LargeText>
      <p>
        <strong>Pro Tip: Unpublished Works</strong>
      </p>
    </LargeText>
    <p>
      Remember that personal communications are considered &apos;unpublished
      works&apos;, which means they cannot be accessed by the reader. Therefore,
      it is important to obtain the sender&apos;s permission before citing their
      communication in your work. Also, the reader cannot verify personal
      communications, so use them sparingly and only when necessary.
    </p>
  </GuideSection>
);

const FinalNotes: React.FC = () => (
  <GuideSection id="FinalNotes">
    <GuideHeader>OSCOLA Style: Final Notes</GuideHeader>
    <p>
      Embarking on your journey of understanding the OSCOLA referencing style
      might initially seem daunting, but with the comprehensive guide provided
      by Bibcitation, you&apos;re now well-equipped to handle the citation
      challenges that lie ahead. From citing books, journals, and legal cases to
      personal communications, you now have the knowledge to create citations
      that adhere to the stringent standards of academic integrity and reflect
      your commitment to scholarly professionalism.
    </p>
    <p>
      In the landscape of legal writing, the ability to format citations
      accurately and swiftly is invaluable. While Bibcitation can aid you in
      this process, having a deep understanding of the principles underpinning
      the OSCOLA referencing style enhances your academic prowess significantly.
      While our citation generator Bibcitation.com can prove to be undeniably
      handy, your newfound expertise in OSCOLA formatting can make you
      self-reliant and confident in your scholarly endeavors.
    </p>
    <p>
      As we navigate through this digital age, the nature and format of academic
      references continue to evolve. However, your knowledge of the OSCOLA
      referencing style, combined with Bibcitation features, ensures that
      you&apos;re equipped to tackle any referencing task that comes your way.
      By learning and applying OSCOLA, you&apos;re not just preparing yourself
      to create accurate citations; you&apos;re readying yourself to contribute
      meaningfully to the rich tapestry of legal scholarship. And remember,
      every perfectly formatted OSCOLA citation is a silent, respectful nod to
      the intellectual contributions of others in your field.
    </p>
  </GuideSection>
);

export default OscolaGuide;
